import React from 'react';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import KwPanel from '../../../common/KwPanel';
import FormSelect from '../../../common/FormSelect';
import FormStatic from '../../../common/FormStatic';
import Button from '../../../common/Button';
import icons from '../../../../constants/icons';
import type {
  KeyProviderOption,
  SteamImportFormValues,
} from './steamGameImportTypes';
import FormTextArea from '../../../common/FormTextArea';

export interface SteamGameImportSetupFormProps {
  register: UseFormRegister<SteamImportFormValues>;
  setValue: UseFormSetValue<SteamImportFormValues>;
  watch: UseFormWatch<SteamImportFormValues>;
  singlePlatformKeyProvider: boolean;
  steamPlatforms: { id: string; name: string }[];
  steamKeyProvidersForSelectedPlatform: KeyProviderOption[];
  packageTypes: { id: string; name: string }[];
  publisherOptions: { id: string; companyName: string }[];
  watchedPlatformId: string;
  onStartImport: () => void;
}

function SteamGameImportSetupForm({
  register,
  setValue,
  watch,
  singlePlatformKeyProvider,
  steamPlatforms,
  steamKeyProvidersForSelectedPlatform,
  packageTypes,
  publisherOptions,
  watchedPlatformId,
  onStartImport,
}: SteamGameImportSetupFormProps) {
  const { t } = useTranslation();

  return (
    <div className="row justify-content-center">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
      >
        <div className="card glass-panel glow-cyan p-4 text-center">
          <FontAwesomeIcon
            icon={icons.CLOUD_ARROW_DOWN}
            size="4x"
            className="text-blue mb-4"
          />
          <h3 className="mb-3">{t('Initialize Import')}</h3>
          <p className="text-secondary mb-4">
            {t(
              'Enter Steam App Ids (comma-separated) to fetch game data and package SKUs from the Steam API.'
            )}
          </p>

          <KwPanel className="mb-0 form-horizontal text-start">
            <FormTextArea
              register={register}
              name="appIds"
              label={t('Steam App Ids')}
              placeholder={t('e.g. 1091500, 1245620, 1086940')}
            ></FormTextArea>

            {!singlePlatformKeyProvider ? (
              <>
                <FormSelect
                  register={register}
                  name="platformId"
                  label={t('Platform')}
                  onChange={() => {
                    setValue('keyProviderId', '');
                  }}
                  testId="select-platform"
                >
                  <option value="">{t('--- choose ---')}</option>
                  {steamPlatforms.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </FormSelect>

                <FormSelect
                  register={register}
                  name="keyProviderId"
                  label={t('Key provider')}
                  disabled={!watchedPlatformId}
                  testId="select-key-provider"
                >
                  <option value="">{t('--- choose ---')}</option>
                  {steamKeyProvidersForSelectedPlatform.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name}
                    </option>
                  ))}
                </FormSelect>
              </>
            ) : (
              <>
                <FormStatic label={t('Platform')}>
                  {steamPlatforms[0]?.name ?? ''}
                </FormStatic>
                <FormStatic label={t('Key provider')}>
                  {steamKeyProvidersForSelectedPlatform[0]?.name ??
                    watch('keyProviderId')}
                </FormStatic>
              </>
            )}

            <FormSelect
              register={register}
              name="packageTypeId"
              label={t('Package type')}
              testId="select-package-type"
            >
              <option value="">{t('--- choose ---')}</option>
              {packageTypes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </FormSelect>

            <FormSelect
              register={register}
              name="publisherCompanyId"
              label={t('Publisher')}
              className="bg-dark border-secondary text-light"
              testId="select-publisher"
            >
              <option value="">{t('--- choose ---')}</option>
              {publisherOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.companyName}
                </option>
              ))}
            </FormSelect>
          </KwPanel>

          <Button
            className="btn btn-primary"
            onClick={onStartImport}
            data-testid="button-start-sync"
            icon={icons.BOLT}
            text={t('Fetch Steam Catalog')}
            title={t('Fetch Steam Catalog')}
          ></Button>
        </div>
      </motion.div>
    </div>
  );
}

export default SteamGameImportSetupForm;
